import React, {useCallback} from 'react';
import classnames from '@src/helpers/classnames';
import {BootstrapColors} from '@src/@types/Bootstrap';
import RenderIf from '@src/components/RenderIf/RenderIf';
import LoadingIndicator from '@src/components/LoadingIndicator/LoadingIndicator';
import Translate from '@src/components/Translate/Translate';

const getLabel = ({label}: { label: React.ReactNode }, id: string): React.ReactNode => {
    if (typeof label === 'string') {
        return <Translate<HTMLLabelElement>
                tag="label"
                className="form-check-label"
                htmlFor={id}
                message={label}
        />;
    }

    if (!React.isValidElement<HTMLLabelElement>(label) || label.props.htmlFor) {
        return label;
    }

    return React.cloneElement(label, {htmlFor: id});
};

type Value = string | boolean | null | undefined;

const loaderStyle = {
    background: 'transparent',
    '--bs-spinner-width': '0.8rem',
    '--bs-spinner-height': '0.8rem',
} as React.CSSProperties;

export default function CustomCheckbox(props: {
    id: string,
    label: string,
    value?: Value,
    uncheckedValue?: Value,
    checkedValue?: Value,
    negativeValue?: Value,
    withNegativeValue?: boolean,
    onChange?: (value: Value) => void,
    bsSize?: 'sm' | 'lg',
    bsColor?: BootstrapColors,
    bsOutline?: boolean,
    containerClassName?: string,
    groupProps?: React.HTMLProps<HTMLDivElement>,
    isLoading?: boolean,
} & Omit<React.HTMLProps<HTMLInputElement>, 'onChange' | 'id' | 'type' | 'defaultValue' | 'value'>) {
    const {withNegativeValue = false} = props;
    const {
        value,
        uncheckedValue = withNegativeValue ? null : false,
        checkedValue = withNegativeValue ? '1' : true,
        negativeValue = withNegativeValue ? '0' : false,
        bsSize,
        bsColor,
        bsOutline,
        containerClassName,
        groupProps = {},
        onChange,
        isLoading = false,
        ...rest
    } = props;

    delete rest.withNegativeValue;

    const onChangeCallback = useCallback(() => {
        if (!onChange) {
            return;
        }

        const values: Value[] = [uncheckedValue, checkedValue];
        if (withNegativeValue) {
            values.push(negativeValue);
        }

        const index = values.findIndex(v => v === value);
        const nextIndex = (index + 1) % values.length;

        onChange(values[nextIndex]);
    }, [uncheckedValue, checkedValue, withNegativeValue, negativeValue, value, onChange]);

    return <div {...groupProps} className={classnames('form-group form-check', {
        [`form-switch-${bsSize}`]: bsSize,
        [`form-checkbox-${bsColor}`]: bsColor,
        'form-checkbox-outline': bsOutline,
        'form-checkbox--negative-value': withNegativeValue && (value === (negativeValue ?? false)),
    }, groupProps.className, containerClassName)}>
        <input
                {...rest}
                id={props.id}
                name={props.id}
                type="checkbox"
                className={classnames('form-check-input cursor-pointer', props.className, {
                    'd-none': isLoading
                })}
                onChange={onChangeCallback}
                checked={value !== uncheckedValue}
        />

        <RenderIf expression={isLoading}>
            <LoadingIndicator
                    type="border"
                    size="sm"
                    className="form-check-input"
                    style={loaderStyle}
            />
        </RenderIf>

        {getLabel(props, props.id)}
    </div>;
}
